<template>
  <v-container>
    <v-row class="justify-space-between">
      <!-- Left side: Order details -->
      <v-col
        cols="12"
        md="7"
      >
        <v-row>
          <v-col
            class="order-md-first"
            cols="12"
            md="6"
          >
            <v-card
              class="px-4"
              elevation="0"
            >
              <div class="font-weight-bold">
                {{ deliveryHeading }}
              </div>

              <div v-if="step === 1 || step === 2">
                <div
                  v-if="deliveryWindow.EarlyTime === 'Unavailable' || deliveryWindow.LateTime === 'Unavailable'"
                  class="font-weight-bold"
                >
                  Unavailable
                </div>
                <div v-else>
                  <div class="font-weight-bold">
                    {{ deliveryWindow.EarlyTime }} - {{ deliveryWindow.LateTime }}
                  </div>
                  <div>{{ deliveryWindow.DeliveryDateString }}</div>
                </div>
              </div>
              <div v-else-if="step === 0">
                Your delivery has been scheduled but has not left the pharmacy at this time.
              </div>
            </v-card>
          </v-col>

          <v-col
            class="order-first d-flex flex-column align-center justify-center"
            cols="12"
            md="6"
          >
            <h3>USPack Order Number: {{ usPackOrderNumber }}</h3>
          </v-col>
        </v-row>

        <v-row>
          <v-col sm="12">
            <order-lifecycle
              :timezone="timezone"
              :step="step"
              :lifecycle-info="lifecycleInfo"
            />
          </v-col>
        </v-row>
      </v-col>

      <!-- Right side: Driver badge -->
      <v-col
        cols="12"
        md="4"
        class="d-flex align-center justify-center"
      >
        <driver-badge
          :driver-code="driverCode"
          :driver-image-url="driverImageUrl"
          :driver-first-name="driverFirstName"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import { TrackingInfoWithEta, USPackOrderNumber } from "@/types/types";
import { defineComponent, PropType } from "vue";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import OrderLifecycle from "./OrderLifecycle.vue";
import DriverBadge from "../common/DriverBadge.vue";

const timezonesLong = new Map();
timezonesLong
  .set("EST", "America/New_York")
  .set("CST", "America/Chicago")
  .set("MDT", "America/Salt_Lake_City")
  .set("PST", "America/Los_Angeles")
  .set("MST", "America/Phoenix")
  .set("AKS", "America/Anchorage")
  .set("HST", "America/Honolulu")

dayjs.extend(utc);
dayjs.extend(timezone);

type Data = {
  step: number;
  lifecycleInfo: {
      PickupDateTime: string,
      DeliveryDateTime: string,
      Eta: string
  };
  deliveryWindow: {
    EarlyTime: string,
    LateTime: string,
    DeliveryDateString: string
  }
  usPackOrderNumber: USPackOrderNumber;
  deliveryHeading: string;
  timezone: boolean;
  driverCode: string;
  driverImageUrl: string;
  driverFirstName: string;
  driverFirstNameUrl: string;
};

export default defineComponent({
  components: {
    "order-lifecycle": OrderLifecycle,
    "driver-badge": DriverBadge,
  },
  props: {
    order: {
      type: Object as PropType<TrackingInfoWithEta>,
      required: true,
    },
  },
  data(): Data {
    return {
      step: 0,
      lifecycleInfo: {
        PickupDateTime: "",
        DeliveryDateTime: "",
        Eta: ""
      },
      deliveryWindow: {
        EarlyTime: "",
        LateTime: "",
        DeliveryDateString: ""
      },
      usPackOrderNumber: 0,
      deliveryHeading: "Delivery Window",
      timezone: true,
      driverCode: "",
      driverImageUrl: "",
      driverFirstNameUrl: "",
      driverFirstName: ""
    };
  },
  created() {
    const { EarlyDateTime, LateDateTime, PickupDateTime, DeliveryDateTime, Eta, Timezone, DriverID, DriverCode } = this.order;

    const timezone = Timezone ? timezonesLong.get(Timezone) : 'UTC';
    this.timezone = Timezone ? true : false;
    this.step = this.getCurrentStep(PickupDateTime, DeliveryDateTime);
    this.usPackOrderNumber = this.order.OrderID;
    this.driverCode = DriverCode != null ? DriverCode.toString() : "";
    this.driverImageUrl =`${process.env.VUE_APP_TRACKING_BACKEND_URL}/driver/${DriverID}/image`;
    this.driverFirstNameUrl = `${process.env.VUE_APP_TRACKING_BACKEND_URL}/drivers/${DriverID}/driver`;

    fetch(this.driverFirstNameUrl)
    .then(res => {
      if (!res.ok) throw new Error("Failed to fetch");
      return res.text();
    })
    .then(data => {
      this.driverFirstName = data || "Driver";
    })
    .catch(err => {
      console.error("Fetch error:", err);
      this.driverFirstName = "Driver";
    });

    const EarlyTime = (EarlyDateTime && EarlyDateTime.trim() !== '')
      ? dayjs.utc(EarlyDateTime).format('hh:mm A').toString()
      : 'Unavailable';

    const LateTime = (LateDateTime && LateDateTime.trim() !== '')
      ? dayjs.utc(LateDateTime).format('hh:mm A').toString()
      : 'Unavailable';

    const DeliveryDateString = (LateDateTime && LateDateTime.trim() !== '')
      ? dayjs.utc(LateDateTime).format('dddd MMM DD, YYYY').toString()
      : 'Unavailable';

    const PickupDateTimeFormatted = dayjs.utc(PickupDateTime).format('dddd MMM DD, YYYY hh:mm A').toString();
    const DeliveryDateTimeFormatted = dayjs.utc(DeliveryDateTime).format('dddd MMM DD, YYYY hh:mm A').toString();
    const EtaFormatted = dayjs.utc(Eta).tz(timezone).format('dddd MMM DD, YYYY hh:mm A').toString();

    Object.assign(this.deliveryWindow, {
      EarlyTime,
      LateTime,
      DeliveryDateString
    })

    Object.assign(this.lifecycleInfo, {
      PickupDateTime: PickupDateTime ? PickupDateTimeFormatted : null,
      DeliveryDateTime: DeliveryDateTime ? DeliveryDateTimeFormatted : null,
      Eta: Eta ? EtaFormatted : null
    });
  },
  methods: {
    getCurrentStep(pickupDateTime: string | null, deliveryDateTime: string | null): number {
      if (deliveryDateTime) {
        return 2;
      } else if (pickupDateTime) {
        return 1;
      } else {
        return 0;
      }
    }
  },
});
</script>